import { PayloadAction } from '@reduxjs/toolkit';
import { isEqual, isNumber, omit, pick } from 'lodash-es';

import { SYNC_WITH_STORAGE_ACTION } from 'state/global-state/global-actions';
import { createAppSlice } from 'state/global-state/utils';
import {
  AvailableRewards,
  IAppliedRewards,
  IRemoveAppliedRewardsAction,
  IResetRewardStateAction,
  IRewardAction,
  IncentiveEvaluationMap,
  LoyaltyUser,
  RewardRedemptionMethod,
} from 'state/loyalty/hooks/types';
import { LoyaltyEngineRewards } from 'state/loyalty/types';

import { userSlice } from '../user/user.slice';

import { IRewardsState } from './rewards.types';
import {
  getAppliedPointsInCart,
  getAppliedRewardsArrayAndTimesApplied,
  getAppliedRewardsFromStorage,
  getStagedCartPoints,
  setStateFromAppliedRewards,
  updateAppliedRewardsInStorage,
} from './rewards.utils';

export const initialState: IRewardsState = {
  appliedLoyaltyRewards: {},
  appliedLoyaltyRewardsArray: null,
  availableLoyaltyRewardsMap: {},
  isPricingRewardApplication: false,
  stagedCartPoints: 0,
  totalTimesRewardApplied: {},
  shouldRefetchRewards: false,
  rewardsFeedbackMap: {},
};

export const rewardsSlice = createAppSlice({
  name: 'rewards',
  initialState,
  reducers: {
    applyReward: (
      state,
      {
        payload,
      }: PayloadAction<
        IRewardAction & {
          redemptionMethod: RewardRedemptionMethod;
          enableRewardUnstacking: boolean;
          loyaltyUser?: LoyaltyUser;
        }
      >
    ) => {
      const {
        cartId,
        rewardBenefitId,
        redemptionMethod = RewardRedemptionMethod.MOBILE,
        quantity = 1,
        enableRewardUnstacking,
      } = payload;
      const {
        appliedLoyaltyRewards,
        availableLoyaltyRewardsMap,
        stagedCartPoints,
        totalTimesRewardApplied,
      } = state;

      const { isApplicable, mappedReward } = validateAppliedReward(
        availableLoyaltyRewardsMap,
        rewardBenefitId,
        totalTimesRewardApplied,
        quantity,
        stagedCartPoints
      );
      if (!isApplicable) {
        return;
      }

      const { pointCost, id: rewardId, sanityId } = mappedReward!;

      if (enableRewardUnstacking) {
        // Restore all used point to then subtract the recently spent points
        state.stagedCartPoints =
          stagedCartPoints +
          Object.values(state.appliedLoyaltyRewards).reduce(
            (acm, { timesApplied, pointCost }) => acm + timesApplied * pointCost,
            0
          ) -
          pointCost * quantity;
      } else {
        state.stagedCartPoints = stagedCartPoints - pointCost * quantity;
      }

      const curAppliedTimes = appliedLoyaltyRewards[cartId]?.timesApplied || 0;
      const newReward = {
        rewardId,
        pointCost,
        rewardBenefitId,
        ...(sanityId ? { sanityId } : null),
        redemptionMethod,
      };

      const updatedAppliedRewards: IAppliedRewards = enableRewardUnstacking
        ? {
            [cartId]: {
              ...newReward,
              timesApplied: 1,
            },
          }
        : {
            ...appliedLoyaltyRewards,
            [cartId]: {
              ...newReward,
              timesApplied: curAppliedTimes + quantity,
            },
          };
      const { appliedRewardsArray, timesRewardApplied } = getAppliedRewardsArrayAndTimesApplied(
        updatedAppliedRewards
      );
      updateAppliedRewardsInStorage(updatedAppliedRewards);
      state.appliedLoyaltyRewards = updatedAppliedRewards;
      state.appliedLoyaltyRewardsArray = appliedRewardsArray;
      state.totalTimesRewardApplied = timesRewardApplied;
    },
    createAvailableLoyaltyRewardsMap: (
      state,
      { payload }: PayloadAction<NonNullable<LoyaltyEngineRewards>>
    ) => {
      const availableRewardMap = payload.reduce((acc: AvailableRewards, reward) => {
        if (!reward?.rewardBenefitId) {
          return acc;
        }

        const rewardInMap = acc[reward.rewardBenefitId];
        // This ensures that the lowest point reward for the benefit item is displayed to the offer in the cart
        const rewardHasLowerPointCost = () => reward.pointCost < rewardInMap.pointCost;

        if (!rewardInMap || rewardHasLowerPointCost()) {
          acc[reward.rewardBenefitId] = reward;
        }

        return acc;
      }, {});

      state.availableLoyaltyRewardsMap = availableRewardMap;
    },
    rehydrateAppliedReward: state => {
      setStateFromAppliedRewards(state, getAppliedRewardsFromStorage());
      const stagedCartPoints = getStagedCartPoints(state, state.stagedCartPoints);
      if (isNumber(stagedCartPoints)) {
        state.stagedCartPoints = stagedCartPoints;
      }
    },
    removeAppliedReward: (state, { payload }: PayloadAction<IRewardAction>) => {
      const { cartId } = payload;
      const { appliedLoyaltyRewards, stagedCartPoints } = state;
      const { pointCost, timesApplied } = appliedLoyaltyRewards[cartId] || {};
      if (pointCost && timesApplied) {
        state.stagedCartPoints = stagedCartPoints + pointCost * timesApplied;
      }
      const updatedAppliedRewards = omit(appliedLoyaltyRewards, cartId);
      setStateFromAppliedRewards(state, updatedAppliedRewards);
    },
    removeAppliedRewards: (state, { payload }: PayloadAction<IRemoveAppliedRewardsAction>) => {
      const { cartIds } = payload;
      if (!cartIds.length) {
        return;
      }

      const { appliedLoyaltyRewards, stagedCartPoints } = state;

      // calculate the total points to be restored
      const restoredPoints = cartIds.reduce((pointsAccum: number, cartId: string) => {
        const { pointCost, timesApplied } = appliedLoyaltyRewards[cartId] || {};

        return pointCost && timesApplied ? pointsAccum + pointCost * timesApplied : pointsAccum;
      }, 0);

      state.stagedCartPoints = stagedCartPoints + restoredPoints;
      const updatedAppliedRewards = omit(appliedLoyaltyRewards, cartIds);
      setStateFromAppliedRewards(state, updatedAppliedRewards);
    },
    resetLoyaltyRewardsState: (state, { payload }: PayloadAction<IResetRewardStateAction>) => {
      const { appliedLoyaltyRewards } = state;
      const { points, shouldResetAvailableRewardsMap = true, redemptionMethod } = payload;

      const updatedAppliedRewards = pick(
        appliedLoyaltyRewards,
        // If set, discard only the rewards with the sent redemption method
        redemptionMethod
          ? Object.keys(appliedLoyaltyRewards).filter(
              key => appliedLoyaltyRewards[key].redemptionMethod !== redemptionMethod
            )
          : []
      );
      setStateFromAppliedRewards(state, updatedAppliedRewards);

      const appliedPoints = getAppliedPointsInCart(state);

      if (shouldResetAvailableRewardsMap) {
        state.availableLoyaltyRewardsMap = {};
      }

      // Prevent the value being 0
      state.stagedCartPoints = Math.max(points - appliedPoints, 0);
      state.shouldRefetchRewards = true;
    },
    setAvailableLoyaltyRewardsMap: (state, { payload }: PayloadAction<AvailableRewards | {}>) => {
      state.availableLoyaltyRewardsMap = payload;
    },
    setIsPricingRewardApplication: (state, { payload }: PayloadAction<boolean>) => {
      state.isPricingRewardApplication = payload;
    },
    setShouldRefetchRewards: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldRefetchRewards = payload;
    },
    setStagedCartPoints: (state, { payload }: PayloadAction<number>) => {
      state.stagedCartPoints = payload;
    },
    setRewardsFeedbackMap: (state, { payload }: PayloadAction<IncentiveEvaluationMap>) => {
      state.rewardsFeedbackMap = payload;
    },
    unApplyReward: (
      state,
      // Not sure if this is the best way to handle loyalty user data. Maybe
      // there's a way to consume data from other slice
      { payload }: PayloadAction<IRewardAction & { loyaltyUser?: LoyaltyUser }>
    ) => {
      const { cartId, loyaltyUser } = payload;
      const { appliedLoyaltyRewards, stagedCartPoints } = state;
      const mappedReward = appliedLoyaltyRewards[cartId];

      if (!mappedReward) {
        return;
      }

      const { pointCost, rewardId, rewardBenefitId, redemptionMethod } = mappedReward;

      // safety check for over subtracting
      const newBalanceExceedsUserBalance =
        stagedCartPoints + pointCost > (loyaltyUser?.points ?? 0);

      if (newBalanceExceedsUserBalance) {
        return;
      }

      state.stagedCartPoints = stagedCartPoints + pointCost;

      const curTimesApplied = appliedLoyaltyRewards[cartId]?.timesApplied || 0;
      const timesApplied = curTimesApplied - 1;
      const updatedAppliedRewards: IAppliedRewards =
        timesApplied > 0
          ? {
              ...appliedLoyaltyRewards,
              [cartId]: {
                timesApplied,
                rewardId,
                pointCost,
                rewardBenefitId,
                redemptionMethod,
              },
            }
          : omit(appliedLoyaltyRewards, cartId);
      setStateFromAppliedRewards(state, updatedAppliedRewards);
    },
  },
  extraReducers: {
    [SYNC_WITH_STORAGE_ACTION]: (state: IRewardsState) => {
      const appliedLoyaltyRewardsFromStorage = getAppliedRewardsFromStorage();
      if (!isEqual(state.appliedLoyaltyRewards, appliedLoyaltyRewardsFromStorage)) {
        state.appliedLoyaltyRewards = appliedLoyaltyRewardsFromStorage;
      }
    },
    [userSlice.actions.setUser.type]: (
      state: IRewardsState,
      { payload }: PayloadAction<LoyaltyUser | null>
    ) => {
      const loyaltyUserPoints = payload?.points ?? 0;
      const stagedCartPoints = getStagedCartPoints(state, loyaltyUserPoints);
      if (isNumber(stagedCartPoints)) {
        state.stagedCartPoints = stagedCartPoints;
      }
    },
  },
});

export function validateAppliedReward(
  availableLoyaltyRewardsMap: AvailableRewards,
  rewardBenefitId: string,
  totalTimesRewardApplied: Record<string, number>,
  quantity: number,
  stagedCartPoints: number
) {
  const mappedReward = availableLoyaltyRewardsMap[rewardBenefitId];
  if (!mappedReward) {
    return { isApplicable: false, mappedReward: null };
  }

  const { pointCost, id: rewardId, limitPerOrder } = mappedReward;

  const timesApplied = totalTimesRewardApplied[rewardId];
  // safety check for over-redemption
  if (pointCost * quantity > stagedCartPoints || timesApplied === limitPerOrder) {
    return { isApplicable: false, mappedReward: null };
  }

  return { isApplicable: true, mappedReward };
}
